<template>
	<div class="financial-gps-wrap">

    <h2>{{ _translate('financialGps.title') }}</h2>

    <div v-if="setStoreTables()" class="report-content">

      <div class="report__block">

        <div class="report__section">

          <h3 class="report__section-heading">{{ _translate('financialGps.riskBudgetFor') }} <span class="report__variable" v-html="getClientName()"></span></h3>
          <p class="report__info">{{ _translate('financialGps.riskBudgetText') }}</p>

          <ul class="report__list">

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('financialGps.clientName') }}</span>
              <span class="report__value" v-html="getClientName()"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('financialGps.currentAge') }}</span>
              <span class="report__value" v-html="getClientAge()"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('financialGps.planStartingYear') }}</span>
              <span class="report__value" v-html="getBaseField('retirement.startPlan')"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('financialGps.currentYear') }}</span>
              <span class="report__value" v-html="getCurrentYear()"></span>
            </li>           

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('financialGps.ageAtRetirement') }}</span>
              <span class="report__value" v-html="getRetirmentAge()"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('financialGps.yearOfRetirement') }}</span>
              <span class="report__value" v-html="getClientRetirementYear()"></span>
            </li>

          </ul>

          <p class="report__info report__info--large">{{ _translate('financialGps.currentValue') }}: <span v-html="formatCurrency(getClientCurrentValue())" class="report__info-highlight"></span></p>

          <ul class="report__list">

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('financialGps.startingAssets') }} <span v-if="getBaseField('retirement.statingAssets') === 'auto'" class="report__list-edit-label">({{ _translate('ui.requiredEntry')}})</span><button v-else class="reset" @click="resetClientStartingAssets"><Icon icon="reset"></Icon><span class="reset__label">{{ _translate('ui.reset') }}</span></button><button v-if="getBaseField('retirement.statingAssets') === 'auto'" class="apply" @click="useSuggestedStartingAssets"><Icon icon="load"></Icon><span class="reset__label">{{ _translate('ui.reset') }}</span></button></span>
              <span class="report__value">
                <div class="input-wrap input-wrap--text">
                  <input id="startingAssets" type="text" data-type="currency" 
                         :class="getAdjustableInputClass('retirement.statingAssets')" 
                         :value="getClientStartingAssets()"
                         :placeholder="getClientStartingAssetsPlaceholder()"
                         @change="saveClientStartingAssets">
                </div>                
              </span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('financialGps.assetGoalAtRetirement') }} <span v-if="getBaseField('retirement.assetsGoal') === 'auto'" class="report__list-edit-label">({{ _translate('ui.requiredEntry')}})</span><button v-if="getBaseField('retirement.assetsGoal') !== 'auto'" class="reset" @click="resetClientGoalAtRetirement"><Icon icon="reset"></Icon><span class="reset__label">{{ _translate('ui.reset') }}</span></button><button v-if="getBaseField('retirement.assetsGoal') === 'auto'" class="apply" @click="useSuggestedClientGoalAtRetirement"><Icon icon="load"></Icon><span class="reset__label">{{ _translate('ui.reset') }}</span></button></span>
              <span class="report__value">
                <div class="input-wrap input-wrap--text">
                  <input id="assetGoal" type="text" data-type="currency" 
                         :class="getAdjustableInputClass('retirement.assetsGoal')" 
                         :value="getClientGloalAtRetirement()"
                         :placeholder="getClientGloalAtRetirementPlaceholder()"
                         @change="saveClientGloalAtRetirement">
                </div>                
              </span>
            </li>

            <li v-if="getClientAge() < getRetirmentAge()" class="report__list-item">
              <span class="report__list-label">{{ _translate('financialGps.annualContributions') }} <span v-if="getBaseField('retirement.annualContributions') === 'auto'" class="report__list-edit-label">({{ _translate('ui.editable') }})</span><button v-if="getBaseField('retirement.annualContributions') !== 'auto'" class="reset" @click="resetClientAnnualContributions"><Icon icon="reset"></Icon><span class="reset__label">{{ _translate('ui.reset') }}</span></button></span>
              <span class="report__value">
                <div class="input-wrap input-wrap--text">
                  <input id="annualContributions" type="text" data-type="currency" 
                         :class="getAdjustableInputClass('retirement.annualContributions')" 
                         :value="getClientAnnualContributions()"
                         @change="saveClientAnnualContributions">
                </div>                
              </span>
            </li>
            <li v-else class="report__list-item">
              <span class="report__list-label">Projected Withdrawal in <span v-html="getCurrentYearWithdrawalYear()"></span></span>
              <span class="report__value" v-html="formatCurrency(getCurrentYearWithdrawal())"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('financialGps.indexation') }}</span>
              <span class="report__value" v-html="getInflationRate()"></span>
            </li>
          </ul>

          <div v-if="showDependencies()">

            <p class="report__info">{{ _translate('financialGps.assumptions')}}</p>
                    
            <ul class="report__list">

              <li v-if="!isRetired()" class="report__list-item">
                <span class="report__list-label">{{ _translate('financialGps.rateOfReturnRequired') }} <span v-if="getBaseField('retirement.rateRequired') === 'auto'" class="report__list-edit-label">({{ _translate('ui.editable') }})</span><button v-if="getBaseField('retirement.rateRequired') !== 'auto'" class="reset" @click="resetInterestRateRequired"><Icon icon="reset"></Icon><span class="reset__label">{{ _translate('ui.reset') }}</span></button></span>
                <span class="report__value">
                  <div class="input-wrap input-wrap--percentage">
                    <input id="rateRequired" type="number" data-type="percentage" 
                           :class="getAdjustableInputClass('retirement.rateRequired')" 
                           :value="getInterestRateRequired()"
                           @change="saveInterestRateRequired">
                  </div>
                </span>
              </li>
              <li class="report__list-item">
                <span class="report__list-label">{{ _translate('financialGps.rateOfReturnRequiredRetirement') }} <span v-if="getBaseField('retirement.retirementRate') === 'auto'" class="report__list-edit-label">({{ _translate('ui.editable') }})</span><button v-if="getBaseField('retirement.retirementRate') !== 'auto'" class="reset" @click="resetInterestRateRetirement"><Icon icon="reset"></Icon><span class="reset__label">{{ _translate('ui.reset') }}</span></button></span>
                <span class="report__value">
                  <div class="input-wrap input-wrap--percentage">
                    <input id="rateRequired" type="number" data-type="percentage" 
                           :class="getAdjustableInputClass('retirement.retirementRate')" 
                           :value="getInterestRateRetirement()"
                           @change="saveInterestRateRetirement">
                  </div>
                </span>
              </li>            

            </ul>

            <p class="report__info report__info--large">{{ _translate('financialGps.adjustmentTitle') }}</p>            

            <ul class="report__list">
              <li class="report__list-item report__list-item--checkbox">
                <Checkbox id="active" :label="_translate('financialGps.useAdjusted')" field="retirement.useAdjusted" value="true"></Checkbox>
              </li>
              <li v-if="getBaseField('retirement.useAdjusted')">
                <p v-if="getBaseField('retirement.useAdjusted')" class="report__info">{{ _translate('financialGps.adjustmentText')}}</p>
                <p v-if="getBaseField('retirement.useAdjusted')" class="report__info">{{ _translate('financialGps.adjustmentText2')}}</p>
              </li>
              <li v-if="getBaseField('retirement.useAdjusted')" class="report__list-item">
                <span class="report__list-label">{{ _translate('financialGps.adjustedGoal') }}</span>
                <span class="report__value adjusted" v-html="formatCurrency(getClientAdjustedGoalAtRetirement())"></span>
              </li>
            </ul>

          </div>

        </div>

        <div class="report__section">

          <h3 class="report__section-heading">{{ _translate('financialGps.financialGpsProjections') }}</h3>

          <div v-if="showDependencies()">

            <p class="report__info">{{ _translate('financialGps.financialGpsProjectionsParagraph1') }}</p>

            <p class="report__info">{{ _translate('financialGps.financialGpsProjectionsParagraph2') }}</p>

            <div v-if="this.$data.report !== 'start'" class="report-ctrl">
            <ul class="report-tabs report-tabs--chart">
              <li :class="setGPSViewClass('savings')"><button class="report-tabs__button" @click="setActiveGPSView('savings', 'GPSProjectionChart', 1, 2)">{{ _translate('financialGps.savings') }}</button></li><!--
           --><li :class="setGPSViewClass('retirement')"><button class="report-tabs__button" @click="setActiveGPSView('retirement', 'GPSProjectionChart', 2, 2)">{{ _translate('financialGps.retirement') }}</button></li>                        
            </ul>

            <button :class="buttonClass('wealthAccumulation', 'list')" @click="buttonChange()"><Icon icon="target"></Icon></button>

            </div>

            <div id="GPSProjectionChart" class="chart-slider">
              <div id="GPSProjectionChart__wrapper" class="chart-slider__wrapper" :style="setInitialGPSView">
                <div class="chart-slider__chart">
                  <Chart type="line" stacked="false" :data="chartWealthProjection('savings')" id="GPSwealthProjection"></Chart>
                </div>
                <div class="chart-slider__chart">
                  <Chart type="line" stacked="false" :data="chartWealthProjection('retirement')" id="GPSwithdrawalProjection"></Chart>
                </div>
              </div>
            </div>

          </div>
          <div v-else v-html="requestStartingAssets()"></div>

        </div>

      </div>

    </div>

  </div>
</template>

<script>

import Icon from '../Icon.vue'
import Checkbox     from '../Checkbox.vue'
import Chart from '../Chart.vue'
import Repeater     from '../Repeater.vue'
import RepeaterForm from '../RepeaterForm.vue'
import TextInput from '../TextInput.vue'

export default {


	components: {

    Icon,
    Checkbox,
    Chart,
    Repeater,
    RepeaterForm,
    TextInput,

	}, 

  /**
   * METHODS
   * -------
   * 
   * These are the methods that are available to the 
   * component.
   */
   
  methods : {


    /**
     * METHOD: setStoreTables()
     * @return {Boolean} - returns true or false, depending on whether or not the table data is set.
     *
     * This method is used to define the table that is used for the Financial GPS.
     * It uses the setFinancialGPSSummary() method to actually fetch the data.
     * Additionally, it checks the postRetirementCashFlowData from the Retirement
     * Planner, which is required for the GPS projection calculations. If there is no 
     * data in the table, the GPS table is not set and the method returns false.
     */
    setStoreTables() {

      if ( window.tables.postRetirementCashFlowData.length > 0 ) { 

        window.tables.financialGPSSummary = this.setFinancialGPSSummary();
        return true;

      }

      return false;

    },



    /**
     * METHOD setFinancialGPSSummary()
     * @return {Object} - returns an object containing all the computed and defined data
     *
     * This method is used as a singular point for defining the entire data object that is used
     * to render the Financial GPS chart. It defines an explicit object, and uses a variety 
     * of other methods to define key data points.
     */
    setFinancialGPSSummary() {

      var isRetired = this.isRetired(),
          useAdjusted = this.getBaseField('retirement.useAdjusted');
      var obj = {

        // Details for rendering charts
        charts: {

          // Defines which chart is used for the projection
          accumulation: (isRetired) ? 'GPSwithdrawalProjectionPDF' : 'GPSwealthProjectionPDF',

          // Defines the title for the chart
          accumulationTitle: (isRetired) ? this._translate('financialGps.retirement') : this._translate('financialGps.savings'),

          // Defines the name of the GPS resilience chart.
          resilience: 'GPSresiliencePDF',

          // Defines details for chart legends.
          legend: {

            fields: [

              {
                field: 'actual',
                name: 'financialGps.actualValue',
                color: '#f9c013',
                width: '*',
              },

              {
                field: 'actual',
                name: 'financialGps.adjustedValue',
                color: '#fe783d',
                width: '*',
              },

              {
                field: 'projected',
                name: 'financialGps.projectedValue',
                color: '#103c63',
                width: '*',
              },

            ],

          },

        },

        // Defines key data points
        details: {

          currentAge: this.getClientAge(),
          retired: isRetired,
          planStartingYear: this.getBaseField('retirement.startPlan'),
          currentYear: this.getCurrentYear(),
          retirementAge: this.getRetirmentAge(),
          retirementYear: this.getClientRetirementYear(),
          currentValue: window.formatCurrency(this.getClientCurrentValue()),
          startingAssets: this.getClientStartingAssets(),
          retirementGoal: this.getClientGloalAtRetirement(),
          annualContributions: this.getClientAnnualContributions(),
          currentYearWithdrawal: window.formatCurrency(this.getCurrentYearWithdrawal()),
          index: this.getInflationRate(),
          requiredRate: this.getInterestRateRequired() + "%",
          retirementRate: this.getInterestRateRetirement() + "%",

        },

        // Defines legend details specifically for resilience.
        resiliance: {

          fields: [

            {
              field: 'event',
              name: 'financialGps.shockEvent',
              width: '*',
            },

            {
              field: 'shock',
              name: 'financialGps.tsxShock',
              width: '*',
            },

            {
              field: 'recovery',
              name: 'financialGps.next12',
              width: '*',
            },

          ],

          values: this.getShockTableValues(),

        },

      };

      // If we're not using adjusted values, remove the adjusted legend item.
      if ( ! useAdjusted ) {
        obj.charts.legend.fields.splice(1,1);
      }

      return obj;

    },    



     /**
     * ---------------------------------------------------------------------------- /
     * 
     * DISPLAY METHODS
     *
     * These methods are used to control how spouse details are rendered within the
     * report component.
     * 
     * --------------------------------------------------------------------------- */


    /**
     * METHOD: requestStartingAssets()
     * @return {String} - the HTML to be included in place of GPS Charts
     *
     * This method simple returns a string to be used in place of the GPS charts when the starting assets and asset goal
     * fields have not been set yet (as those values are required for the calculations that power the charts.
     */
    requestStartingAssets() {

      return '<p class="report__info" style="margin-bottom: .5rem">The <i>Starting Assets</i> and <i>Asset Goal at Retirement</i> fields value must be set before this section can be displayed.</p>';

    },


    /**
     * METHOD: showDependencies()
     * @return {Boolean} - return boolean value indicating whether or not dependencies are met.
     *
     * This method checks to ensure that the starting assets and assets goal fields are not set to their default
     * value of 'auto' (which cannot be used for the calculations). The component uses this method to determine whether
     * or not to render certain key parts of the report.
     */
    showDependencies() {

      return (
        this.getBaseField('retirement.statingAssets') !== 'auto' &&
        this.getBaseField('retirement.assetsGoal') !== 'auto');

    },


    /**
     * METHOD: isRetired()
     * @return {Boolean} - returns whether or not the client has reached the age of retirement.
     *
     * This method is used to determine whether or not the client has reached the age of retirement,
     * as defined in the client profile.
     */
    isRetired() {

      return this.getClientAge() >= this.getRetirmentAge()

    },


    /**
     * METHOD: getAdjustableInputClass()
     * @param  {String} field - the client field that the data control should be using
     * @return {String} - the class text that is used in the HTML input.
     *
     * This method is used to control the class of certain inputs within the form's own controls.
     * There are certain values that can (or must) be changed by the user. Each of these fields
     * contains a corresponding field within the client profile. By default, these values are
     * set to 'auto'. The method works by checking for the default value in the client field.
     * If it is not found, it assumes a custom value and appends an additional class, which
     * the CSS uses to change the colour of the field as a visual indicator that the value has 
     * been set manually.
     */
    getAdjustableInputClass(field) {

      var c = 'input input--text';

      if ( this.getBaseField(field) !== 'auto' ) {

        c += ' input--custom-val';

      }

      return c;

    },


    /**
     * METHOD: buttonClass()
     * @return {String} - the class text that is used in the HTML input.
     *
     * This method is used to add a class to the GPS Mode button that appears with the GPS chart,
     * and which allows the user to toggle the way target total asset values are plotted on
     * the chart. When the buttons is active, an additional class is added, allowing the CSS
     * to change the colour of the icon to indicate its active state.
     */
    buttonClass() {

      var c = 'section__button';

      if ( this.$data.gpsMode === 'extended' ) {

        c += ' js-active';

      }

      return c;

    },


    /**
     * METHOD: buttonChange()
     *
     * This method is triggered when the GPS Mode button is clicked. It uses a basic 
     * ternary assignment to toggle between the values of 'basic' and 'extended' 
     * whenever the button is clicked.
     */
    buttonChange() {

      this.$data.gpsMode = (this.$data.gpsMode === 'basic') ? 'extended' : 'basic';

    },    


    /**
     * METHOD: setGPSViewClass()
     * @param {String} tab - a string representing the the version of the chart that should be shown.
     *
     * This method is used to control the 'tab' that is shown on the GPS chart. This basically translates
     * to which version of the chart should be actively shown: the savings or post-retirement. This
     * is achieved by setting the js-active state in a pseudo-slider.
     */
    setGPSViewClass(tab) {

      var c = 'report-tabs__item';

      if ( tab === this.autoCheckGPSView() ) {

        c += ' js-active';

      } 

      return c;
      
    }, 


    /**
     * METHOD: setActiveGPSView()
     * @param {String} tab    - a string of the view that should be set.
     * @param {String} target - the target identifier within the component HTML
     * @param {Number} i      - the index number for the target slide, used to calculate offset
     * @param {Number} total  - the total number of slides, used to calculate offset
     *
     * This method is triggered when the tab buttons are clicked. It uses the supplied values to 
     * control the pseudo-slider that contains the charts.
     */
    setActiveGPSView(tab, target, i, total) {

      this.$data.gpsView = tab;

      var slider = document.getElementById(target + '__wrapper'),
          p = ((i - 1) / total) * 100;

      slider.style.transform = 'translate3d(-' + p + '%, 0, 0)';

    },


    /**
     * METHOD: autoCheckGPSView()
     * @return - return the GPS view string
     *
     * This method returns the GPS view string that is used to control the active "tab" of the GPS
     * chart pseudo-slider. The method checks to determine if this value is set to 'auto'. If it is,
     * it determines whether or not the client is in retirement and returns the value based on that.
     * Otherwise, it returns the stored value in the component data.
     */
    autoCheckGPSView() {

      if ( this.$data.gpsView === 'auto' ) {

        var view = 'savings',
            retirement = Number(this.getClientRetirementYear()),
            year = Number(this.$store.getters.getCurrentYear());

        if ( year > retirement ) {

          view = 'retirement';

        }

        return view;

      }

      return this.$data.gpsView;

    },   


     /**
     * ---------------------------------------------------------------------------- /
     * 
     * FORMATTING METHODS
     *
     * These methods are used to format the output of certain types of values
     * within the report.
     * 
     * --------------------------------------------------------------------------- */

    /**
     * METHOD: formatCurrency()
     * @param  {number} val - the numeric value that should be converted
     * @return {string} the value, formatted as a currency string
     *
     * This method is designed to connect into the global formatCurrency()
     * method defined at the window level.
     */
    formatCurrency(val) {

      return window.formatCurrency(val);

    },


    /**
     * METHOD: formatPercentage()
     * @param  {number} number - the numeric value that should be converted
     * @return {string} the value, formatted as a percentage
     *
     * This method is designed to convert a numeric value into a percentage string.
     */
    formatPercentage(val) {

      return window.formatPercentage(val);

    },


    /**
     * METHOD: stripFormatCurrency()
     * @param  {String} val - the value to convert
     * @return {Number} - returns the value, converted into a number
     *
     * Occasionally, it is necessary a currency string back into an actual number.
     * This method is designed to take a dollar value in string format and convert it
     * back into a number. The method assumes that the string was created using the
     * global formatCurrency() method defined at the window level. It also attempts
     * to account for French string formats if the application language is set to French.
     */
    stripFormatCurrency(val) {

      var lang = this.$store.getters.getCurrentLanguage;
       
      if ( lang === 'fr' ) {
          val = Number(val.replace(/\$/g, '').replace(/,/g,'.').replace(/ /g,'')) ; 
      } else {
          val = Number(val.replace(/\$/g, '').replace(/,/g,'')); 
      }

      return val;

    },



    /**
     * ---------------------------------------------------------------------------- /
     * 
     * BASE REPORTING METHODS
     *
     * These methods are used to retrieve certain key pieces of data for use in
     * the report.
     * 
     * --------------------------------------------------------------------------- */
    

    /**
     * METHOD: getBaseField()
     * @param  {String} field - the name of the field to be retrieved
     * @return {Various} - return the value of the requested field.
     *
     * This method is a simple retrieval function, which passes the value of the
     * field parameter to the getFieldData() getter method and returns the result.
     */
    getBaseField(field) {

      return this.$store.getters.getFieldData({field: field});

    },


    /**
     * METHOD: getClientName()
     * @return {String} - return the client name
     *
     * This method is a simple wrapper to access the getCurrentClientName() getter method from the store.
     */
    getClientName() {

      return this.$store.getters.getCurrentClientName();

    },


    /**
     * METHOD: getClientAge()
     * @return {Number} - return the client name
     *
     * This method is a simple wrapper to access the getClientAge() getter method from the store.
     */
    getClientAge() {

      return this.$store.getters.getClientAge();

    },


    /**
     * METHOD: getRetirmentAge()
     * @return {Number} - the age at which the client will retire
     *
     * This method calls the getRetirmentAge() getter function to retrieve the
     * age at which the client will retire. This value is determined either by the
     * explicit age value or by the retirment date field.
     */
    getRetirmentAge() {

      return this.$store.getters.getRetirmentAge();

    },     


    /**
     * METHOD: getClientBirthMonth()
     * @return {Number} - return the date of birth month
     *
     * This method is a simple wrapper to access the getClientDOB() getter method from the store.
     * The method also enforces the month as a numeric value
     */
    getClientBirthMonth() {

      var dob = this.$store.getters.getClientDOB();
      return Number(dob.month);

    },    


    /**
     * METHOD: getInflationRate()
     * @return {Number} - return the client name
     *
     * This method is a simple wrapper to access the getIndexPercentage() getter method from the store.
     */
    getInflationRate() {

      return this.$store.getters.getIndexPercentage();

    },


    /**
     * METHOD: getCurrentYear()
     * @return {Number} - return the client name
     *
     * This method is a simple wrapper to access the getCurrentYear() getter method from the store.
     */
    getCurrentYear() {

      return this.$store.getters.getCurrentYear();

    },


    /**
     * METHOD: getClientRetirementYear()
     * @return {Number} - return the client name
     *
     * This method is a simple wrapper to access the getClientRetirementYear() getter method from the store.
     */
    getClientRetirementYear() {

      return this.$store.getters.getClientRetirementYear();

    },


    /**
     * METHOD: getClientCurrentValue()
     * @return {Number} - return the client name
     *
     * This method is a simple wrapper to access the getAssetsCurrent() getter method from the store.
     */
    getClientCurrentValue() {

      return this.$store.getters.getAssetsCurrent();

    },


    /**
     * METHOD: getClientStartingAssets()
     * @return {String} - return the starting assets value
     *
     * This method uses the getBaseField() method to retrieve the value of the starting assets data
     * point within the Financial GPS component. If the value is 'auto' (meaning it has not been set),
     * it returns an empty string. In this instance, the input will display the auto-generated value
     * as placeholder text.
     */
    getClientStartingAssets() {

      var assets = this.getBaseField('retirement.statingAssets');

      if ( assets === 'auto' ) {

        return '';

      }

      assets = Number(assets);
      return window.formatCurrency(assets);

    },


    /**
     * METHOD: getClientStartingAssetsPlaceholder()
     * @param  {Boolean} format - defines whether or not to apply currency formatting to the output.
     * @return {String/Number} - returns the calculated initial capital value
     *
     * This method is used to get the placeholder value for the initial capital data point within
     * the Financial GPS report. It allows for the option for formatting or not formatting. The formatted
     * version is used to display the value in the placeholder attribute of the input. The non-formatted
     * version is used to apply that value to the field in the client profile, through the 
     * useSuggestedStartingAssets() method.
     */
    getClientStartingAssetsPlaceholder(format = true) {

      if ( format ) {

        return this.formatCurrency(this.$store.getters.getInitialCapital());

      }

      return this.$store.getters.getInitialCapital();

    },      


    /**
     * METHOD getClientGloalAtRetirement()
     * @return {Number} - return the value of the client's goal at retirement.
     *
     * This method uses the getBaseField() method to retrieve the value of the client's retirement goal
     * within the Financial GPS component. If the value is 'auto' (meaning it has not been set),
     * it returns an empty string. In this instance, the input will display the auto-generated value
     * as placeholder text.
     */
    getClientGloalAtRetirement() {

      var goal = this.getBaseField('retirement.assetsGoal');

      if (typeof goal === 'string' || goal instanceof String) {

        if ( goal.substr(0, 4) === 'auto' ) {

          return '';

        }

      }

      goal = Number(goal);
      return window.formatCurrency(goal);

    },


    getClientAdjustedGoalAtRetirement() {
      
      var rawData = this.getWealthProjectionData(true),
          goal = 0;


      if ( Object.entries(rawData).length > 0 ) {

        for ( var y in rawData ) {

          if ( rawData[y] > goal ) {

            goal = Number(rawData[y]);

          }

        }

      }

      return goal;

    },


    /**
     * METHOD: getClientGloalAtRetirementPlaceholder()
     * @param  {Boolean} format - defines whether or not to apply currency formatting to the output.
     * @return {String/Number} - returns the calculated retirement goal value
     *
     * This method is used to get the placeholder value for the initial capital data point within
     * the Financial GPS report. It allows for the option for formatting or not formatting. The formatted
     * version is used to display the value in the placeholder attribute of the input. The non-formatted
     * version is used to apply that value to the field in the client profile, through the 
     * useSuggestedStartingAssets() method.
     */ 
    getClientGloalAtRetirementPlaceholder(format = true) {

      if ( format ) {

        return this.formatCurrency(this.$store.getters.getRetirementCapitalAvailable());

      }

      return this.$store.getters.getRetirementCapitalAvailable();

    }, 


    /**
     * METHOD: getClientAnnualContributions()
     * @return {Number} - returns the annual contributions value
     *
     * This method is used to get the the total annual contributions value in the client profile.
     * If the value is set to 'auto', it calculates a value automatically, using the 
     * getTotalContributions() method. Otherwise, it simply converts the value to a number
     * and returns it.
     */ 
    getClientAnnualContributions() {

      var contribute = this.getBaseField('retirement.annualContributions');

      if ( contribute === 'auto' ) {

        contribute = this.$store.getters.getTotalContributions();

      }

      contribute = Number(contribute);
      return window.formatCurrency(contribute);

    }, 


    /**
     * METHOD: getInterestRateRequired()
     * @return {String} - returns the calculated interest rate required.
     *
     * This method is used to get the calculated required rate of return to meet the goal. It checks 
     * to see if the value has been explicitly defined in the client profile. If it is defined,
     * it returns that value. Otherwise the method uses the getInterestRateRequired() getter method 
     * to calculate the value before returning it.
     */
    getInterestRateRequired() {

      var rate = this.getBaseField('retirement.rateRequired');

      if ( rate === 'auto' ) { 

        var val = this.getClientGloalAtRetirement();
            val = this.stripFormatCurrency(val)

        var contribute = this.getClientAnnualContributions();
            contribute = this.stripFormatCurrency(contribute) 

        var capital = this.getClientStartingAssets();
            capital = this.stripFormatCurrency(capital)

        rate = this.$store.getters.getInterestRateRequired(
          this.$store.getters.getClientYearsToRetire(),
          contribute,
          capital,
          val,
          0 // A type of 0 tells the method to calculate accumulation pre-retirement
        );
      }

      // A little mathemagics to round the value to a decimal percentage.
      rate *= 10000;
      rate = Math.ceil(rate);
      rate /= 100;

      return rate.toFixed(2);

    },           


    /**
     * METHOD: getInterestRateRetirement()
     * @return {String} - returns the calculated interest rate required.
     *
     * This method is used to get the calculated required rate of return through retirement. It checks 
     * to see if the value has been explicitly defined in the client profile. If it is defined,
     * it returns that value. Otherwise the method uses the getInterestRateRequired() getter method 
     * to calculate the value before returning it.
     */
    getInterestRateRetirement() {

      var rate = this.getBaseField('retirement.retirementRate');

      if ( rate === 'auto' ) {

        // rate = Number(this.getInterestRateRequired());
        
        var avg = this.getAverageYearlyWithdrawal() * -1;

        var val = this.getClientGloalAtRetirement();            
            val = this.stripFormatCurrency(val);
        
        rate = this.$store.getters.getInterestRateRequired(
          this.$store.getters.getClientWidrawaltoDeath(),
          false,
          val,
          0,
          1 // A type of 1 tells the method to calculate rate through re-accumulation in retirement
        );        

      }

      // A little mathemagics to round the value to a decimal percentage.
      rate *= 10000;
      rate = Math.ceil(rate);
      rate /= 100;

      return rate.toFixed(2); 

    },


    /**
     * METHOD: getWealthProjectionData()
     * @param  {Boolean} adjusted - defines whether or not to apply currency formatting to the output.
     * @return {Array} - returns an array of projected value points.
     *
     * This method projects the total value of the client's assets through pre-retirement
     * years. It checks the gpsMode value from the component data to help control how 
     * the data is distributed. The 'basic' mode applies a single value for each year,
     * while 'extended' mode the method adds a single entry within sets of 12, to account
     * for the monthly display. The other 11 months per year are filled with a value of
     * 'null' so that the chart does not render any value, but emulates a curve identical
     * to the one rendered in 'basic' mode.
     */
    getWealthProjectionData(adjusted = false) {

      var lang = this.$store.getters.getCurrentLanguage,
          mode = this.$data.gpsMode;  

      var startYear = Number(this.getBaseField('retirement.startPlan')),
          endYear   = Number(this.getClientRetirementYear()),
          diffYear  = endYear - startYear,

          rate   = Number(this.getInterestRateRequired()) / 100,

          contribute = this.getClientAnnualContributions(),

          val    = this.getClientStartingAssets(),
          values = [];

        contribute = this.stripFormatCurrency(contribute) 
        val = this.stripFormatCurrency(val);

      if ( adjusted ) {
        var adjustments = this.$store.getters.getAdjustmentValues();
      }

      // Break the projections out over the course of years
      if ( mode === 'basic' ) {

        for ( var x = startYear; x <= endYear; x++ ) {

          var key = '_' + x;
          values[key] = val;
          val = val * (1 + rate);
          if ( adjusted ) {
            if ( adjustments[key] !== undefined ) {
              val += adjustments[key];
            } else {
              val += contribute;
            }
          } else {
            val += contribute;
          }

        }

      }

      // Break the projections out over 12 month periods within the course of years
      else if ( mode === 'extended' ) {

        var point = this.getClientBirthMonth(),
            m = point;

        for ( var x = startYear; x <= endYear; x++ ) {

          m = ( x === startYear ) ? 1 : point;

          for ( var y = 1; y <= 12; y++ ) {

            // If this is the desired point in the year, store the value;
            // Otherwise, set the value to null.
            if ( ! ( x === endYear && y > m ) ) {
              values[('_' + x) + ('_' + y)] = (y === m) ? val : null;
            }

          }

          val = val * (1 + rate);

          var key = '_' + x;
          if ( adjusted ) {
            if ( adjustments[key] !== undefined ) {
              val += adjustments[key];
            } else {
              val += contribute;
            }
          } else {
            val += contribute;
          }



        }       

      }

      return values;

    },


    /**
     * METHOD: getCurrentYearWithdrawal()
     * @return {Number} - return the withdrawal from the current year.
     *
     * This method gets the projected withdrawal for the given year. This data is only
     * used in retirement, and is displayed in place of the annual contributions value.
     * Because this value changes year over year (due to a number of factors), we use
     * the current year's value for display.
     */
    getCurrentYearWithdrawal() {

      var rawData = window.tables.postRetirementCashFlowData,
          year = this.getCurrentYear(),
          total = 0,
          past = this.$store.getters.getClientDOBPast();

      if ( past ) { year += 1; }

      for ( var x = 0; x < rawData.length; x++ ) {

        if ( Number(rawData[x].year) === Number(year) ) {
          
          total = rawData[x].required;

          if (total === undefined || total === NaN) { 

            total = 0;
            for ( var k in rawData[x] ) {

              if ( k === 'savings' ||
                   k === 'tfsa' ||
                   k === 'rrsp' ||
                   k === 'lockedin' ) {

                total += Number(rawData[x][k]);

              }

            }

          }          

        }

      }

      return total;

    },


    /**
     * METHOD: getCurrentYearWithdrawalYear()
     * @return {Number} - return the current year as a number
     *
     * This method simply gets the current year. If the client's birthday is already
     * passed, it will use the next year.
     */
    getCurrentYearWithdrawalYear() {

      var past = this.$store.getters.getClientDOBPast(),
          year = this.getCurrentYear();

      if ( past ) { year += 1; }

      return year;

    },


    /**
     * METHOD: getAverageYearlyWithdrawal()
     * @return {Number} - returns the average yearly withdrawal.
     *
     * This method basically calculates the average annual withdrawal, based on the data
     * in the postRetirementCashFlowData table. This average value can then be used by
     * the getInterestRateRetirement() method to help calculate the approximate rate of
     * return required in retirement.
     */
    getAverageYearlyWithdrawal() {

      var rawData = window.tables.postRetirementCashFlowData,
          total = 0,
          avg = 0;

      for ( var x = 0; x < rawData.length; x++ ) {

        total += rawData[x].required;

      }

      avg = total / x;

      return avg;

    },


    /**
     * METHOD: getWealthDeaccumulationData()
     * @return {Object} - return an object containing all of the wealth deaccumulation data
     *
     * This method is used to calculate and tabulate all of the data used to create the
     * Financial GPS chart for post-retirement. 
     */
    getWealthDeaccumulationData(adjusted = false) {

      var data        = {},
          rawData     = window.tables.postRetirementCashFlowData,
          mode        = this.$data.gpsMode,
          wealthData  = this.getWealthProjectionData(adjusted),
          startAge    = this.getRetirmentAge(),
          endAge      = this.getBaseField('retirement.lifeExpectancy'),
          dob         = this.$store.getters.getClientDOB(),
          dobYear     = Number(dob.year),

          rate   = Number(this.getInterestRateRetirement()) / 100,

          wealth = 0;         

      if ( adjusted ) {
        var adjustments = this.$store.getters.getAdjustmentValues(true);
      }

      var x = '_0';

      // Start by defining the total amount of wealth AT retirement.
      // If we have wealth data, we get the total of the final year of accumulation...
      if ( Object.entries(wealthData).length > 0 ) {

        for ( var y in wealthData ) {

          if ( y > x ) {

            wealth = Number(wealthData[y]);

          }

        }

      }

      // Otherwise, get the entered value goal.
      else {

        wealth = this.getClientGloalAtRetirement();
        wealth = this.stripFormatCurrency(wealth)

      }

      var start = true,
          m = Number(dob.month);

      // Now, let's loop over the post retirement cashflow data, which will provide
      // us with the withdrawal amounts that we need.    
      for ( var x = 0; x < rawData.length; x++ ) {
        

        // In 'basic' mode, we just assign total remaining wealth
        // to each year.
        if ( mode === 'basic' ) {

          if ( start ) {

            start = false;

          }

          else {

            var remainder = rawData[x - 1].required;

            if ( adjusted ) {
              var yearKey = '_' + rawData[x - 1].year;
              if ( adjustments[yearKey] !== undefined ) {
                remainder = adjustments[yearKey];
              }
            }

            wealth -= remainder;
            wealth *= (1 + rate);

          } 

          data['_' + rawData[x].year] = wealth;

        }

        // Otherwise, we need to get more elaborate, and break things out
        // by month.
        else { 

          if ( start ) {

            start = false;

            for ( var y = 1; y < 12; y++ ) {

              var i = ('_' + rawData[x].year + '_' + y);

              if ( y === 1 ) {
              
                data[i] = wealth;

              }

              else {

                data[i] = null;

              }

            }

          }

          else {

            for ( var y = 1; y < 12; y++ ) {

              var i = ('_' + rawData[x].year + '_' + y);

              if ( y === m ) {

                var remainder = rawData[x - 1].required;

                if ( adjusted ) {
                  var yearKey = '_' + rawData[x - 1].year;
                  if ( adjustments[yearKey] !== undefined ) {
                    remainder = adjustments[yearKey];
                  }
                }

                wealth -= remainder;
                wealth *= (1 + rate);

                data[i] = wealth; 

              }

              else {

                data[i] = null

              }                   

            }

          } 

        }
          
      }

      wealth -= rawData[x - 1].required;

      if ( mode === 'basic' ) {

        data['_' + (Number(rawData[x - 1].year) + 1)] = wealth; //(wealth > 0) ? wealth : 0;

      } 

      else {

        data['_' + (Number(rawData[x - 1].year) + 1) + '_1'] = wealth; //(wealth > 0) ? wealth : 0;

      }

      return data;

    },


    /**
     * METHOD: getWealthActualData()
     * @return {Object} - return an object containing annual values over time.
     *
     * This method is used to get the actual values stored in the Annual Values Over Time
     * repeater field. It also uses the component's gpsMode data attribute to determine
     * whether or not to plot the values against a yearly or monthly value. 
     *
     * If gpsMode is set to 'basic', the values in the same year will be averaged together and
     * only these averages will be designated by year.
     *
     * If gpsMode is set to 'extended', all the values will be included, designated by 
     * month and year.
     */
    getWealthActualData() {

      var rawData = this.$store.getters.getRepeaterData({field: 'savings.periodicValues.values'}),
          parsedData = {},
          finalData  = {},
          year = this.getCurrentYear(),
          mode = this.gpsMode;

      for ( var x = 0; x < rawData.length; x++ ) {

        var r = false,
            v = false,
            m = false;

        for ( var y = 0; y < rawData[x].length; y++ ) {

          var entry = {};
          for ( var k in rawData[x][y] ) {

            entry[k] = rawData[x][y][k];

          }

          if ( entry.field === 'year' ) { 

            r = entry.value;

          }

          if ( entry.field === 'balance' ) {

            v = entry.value;

          }

          if ( entry.field === 'month') {

            m = Number(entry.value);

          }

          if ( r && v ) {

            if ( mode === 'extended' ) {

              r = r + '_' + m;

            }

            if ( parsedData[r] === undefined ) {

              parsedData[r] = [];

            }

            parsedData[r].push(v);

          }

        }

      }
      
      for ( var k in parsedData) {

        var total = 0;

        if ( year === k ) {

          finalData['_' + k] = Number(parsedData[k][parsedData[k].length - 1]);

        }

        else {

          for ( var x = 0; x < parsedData[k].length; x++ ) {

            total += Number(parsedData[k][x]);

          }
          finalData['_' + k] = total / parsedData[k].length;

        }

      }   

      return finalData;

    },


    /**
     * METHOD: getPostRetirementCashFlowData()
     * @return {Object} - return the postRetirementCashFlowData data table object
     *
     * This method functions essentially as a reference method to access the postRetirementCashFlowData
     * data from the global tables.
     */
    getPostRetirementCashFlowData() {

      return window.tables.postRetirementCashFlowData;

    },


    /**
     * METHOD: getDisasterDetails()
     * @return {Array} - return an array with all the disaster recovery data
     *
     * This method gets the information from the disaster recovery repeater field in the application
     * constants and converts the repeater data into a simple array of objects.
     */
    getDisasterDetails() {

      var rawData = this.$store.getters.getConstants(),
          data = [];

      for ( var x = 0; x < rawData.shockEvents.rows.length; x++ ) {

        var row = {};

        for ( var y = 0; y < rawData.shockEvents.rows[x].length; y++ ) {

          if ( rawData.shockEvents.rows[x][y].field === 'name' ) {

            row.name = rawData.shockEvents.rows[x][y].value;

          }

          if ( rawData.shockEvents.rows[x][y].field === 'shock' ) {

            row.shock = Number(rawData.shockEvents.rows[x][y].value);

          }

          if ( rawData.shockEvents.rows[x][y].field === 'next12' ) {

            row.next12 = Number(rawData.shockEvents.rows[x][y].value);

          }

        }

        data[x] = row;

      }

      return data;

    },


    /** 
     * METHOD: getTableItem()
     * @param  {Object} item - the object to be checked
     * @param  {String} property - the key to check against
     * @return {Various} - returns the checked value
     *
     * This method is an extremely simple helper method which accepts an object, and a string
     * key, then uses the key to return a value from the object. Exists primarily to simplify
     * the template stuctures.
     */
    getTableItem(item, property) {

      return item[property];

    },


    /**
     * METHOD: getShockTableValues()
     * @return {Array} - an array of the disaster recovery values
     *
     * This method takes the array generated by the getDisasterDetails() method and
     * converts it to an array that can be used to display details in the generated
     * client PDF.
     */
    getShockTableValues() {

      var rawData = this.getDisasterDetails(),
          obj = []

      for ( var x = 0; x < rawData.length; x++ ) {

        obj[x] = {

          event: rawData[x].name,
          shock: Number(rawData[x].shock * 100).toFixed(2) + '%',
          recovery: Number(rawData[x].next12 * 100).toFixed(2) + '%',

        }

      }

      return obj;

    },    



    /**
     * ---------------------------------------------------------------------------- /
     * 
     * SAVE METHODS
     *
     * These methods are used to save configurable data points within the report.
     * 
     * --------------------------------------------------------------------------- */


    /**
     * METHOD: saveClientGloalAtRetirement()
     * @param  {Event} e - the event that triggered the function
     *
     * This method is triggered when the value of the Goal at Retirement input is changed.
     * The method dispatches the saved value back to the store, then adjusts the displayed
     * value to ensure that it includes dollar formatting.
     */
    saveClientGloalAtRetirement(e) {

      var val  = e.target.value;

      val = this.stripFormatCurrency(val)

      this.$store.dispatch('saveField', {
        field: 'retirement.assetsGoal',
        value: Number(val),
      }); 

      e.target.value = val.toFixed(2);      

    },


    /**
     * METHOD: saveClientStartingAssets()
     * @param  {Event} e - the event that triggered the function
     *
     * This method is triggered when the value of the Starting Assets input is changed.
     * The method dispatches the saved value back to the store, then adjusts the displayed
     * value to ensure that it includes dollar formatting.
     */
    saveClientStartingAssets(e) {

      var val  = e.target.value;

      if ( val === '' ) {

        val = 'auto';

      }

      else {

        val = this.stripFormatCurrency(val)

      }

      this.$store.dispatch('saveField', {
        field: 'retirement.statingAssets',
        value: val,
      }); 

      e.target.value = val.toFixed(2);    

    },

    /**
     * METHOD: saveClientAnnualContributions()
     * @param  {Event} e - the event that triggered the function
     *
     * This method is triggered when the value of the Annual Contributions input is changed.
     * The method dispatches the saved value back to the store, then adjusts the displayed
     * value to ensure that it includes dollar formatting.
     */
    saveClientAnnualContributions(e) {

      var val  = e.target.value;

      val = this.stripFormatCurrency(val)

      this.$store.dispatch('saveField', {
        field: 'retirement.annualContributions',
        value: Number(val),
      }); 

      e.target.value = val.toFixed(2);      

    },


    /**
     * METHOD: saveInterestRateRequired()
     * @param  {Event} e - the event that triggered the function
     *
     * This method is triggered when the value of the Rate of Return Required input is changed.
     * The method dispatches the saved value back to the store, then adjusts the displayed
     * value to ensure that it includes dollar formatting.
     */
    saveInterestRateRequired(e) {

      var val  = Number(e.target.value);

      this.$store.dispatch('saveField', {
        field: 'retirement.rateRequired',
        value: val / 100,
      }); 

      e.target.value = val.toFixed(2);     

    },


    /**
     * METHOD: saveInterestRateRetirement()
     * @param  {Event} e - the event that triggered the function
     *
     * This method is triggered when the value of the Rate of Return Required in Retirement input is changed.
     * The method dispatches the saved value back to the store, then adjusts the displayed
     * value to ensure that it includes dollar formatting.
     */
    saveInterestRateRetirement(e) {

      var val  = Number(e.target.value);

      this.$store.dispatch('saveField', {
        field: 'retirement.retirementRate',
        value: val / 100,
      }); 

      e.target.value = val.toFixed(2);     

    },    



     /**
     * ---------------------------------------------------------------------------- /
     * 
     * RESET METHODS
     *
     * These methods are used to reset certain key data points within the component.
     *
     * TO DO: These methods can be re-purposed into a single reset method.
     * 
     * --------------------------------------------------------------------------- */

    /**
     * resetClientStartingAssets()
     * 
     * This method is activated when the reset button is pressed on the
     * Client Starting assets input. The method essentially reverts the value
     * in the client profile back to the default 'auto'
     */
    resetClientStartingAssets() {

      this.$store.dispatch('saveField', {
        field: 'retirement.statingAssets',
        value: 'auto',
      });

    }, 

    /**
     * resetClientGoalAtRetirement()
     * 
     * This method is activated when the reset button is pressed on the
     * Client Retirement Goal input. The method essentially reverts the value
     * in the client profile back to the default 'auto'
     */
    resetClientGoalAtRetirement() {

      this.$store.dispatch('saveField', {
        field: 'retirement.assetsGoal',
        value: 'auto',
      });

    },

    /**
     * resetClientAnnualContributions()
     * 
     * This method is activated when the reset button is pressed on the
     * Annual Contributions input. The method essentially reverts the value
     * in the client profile back to the default 'auto'
     */
    resetClientAnnualContributions() {

      this.$store.dispatch('saveField', {
        field: 'retirement.annualContributions',
        value: 'auto',
      });

    }, 

    /**
     * resetInterestRateRequired()
     * 
     * This method is activated when the reset button is pressed on the
     * Interest Rate Required input. The method essentially reverts the value
     * in the client profile back to the default 'auto'
     */
    resetInterestRateRequired() {

      this.$store.dispatch('saveField', {
        field: 'retirement.rateRequired',
        value: 'auto',
      });

    },    

    /**
     * resetInterestRateRetirement()
     * 
     * This method is activated when the reset button is pressed on the
     * Interest Rate Required in Retirement input. The method essentially reverts the value
     * in the client profile back to the default 'auto'
     */
    resetInterestRateRetirement() {

      this.$store.dispatch('saveField', {
        field: 'retirement.retirementRate',
        value: 'auto',
      });

    },    




     /**
     * ---------------------------------------------------------------------------- /
     * 
     * SUGGESTED VALUE METHODS
     *
     * These methods are used to apply an auto-calculated value to key data points
     * within the application. These calculations are generally used to pull data
     * from the Retirement Planner data and replace the 'auto' value of the 
     * respective data points within the client profile.
     * 
     * --------------------------------------------------------------------------- */


    /**
     * METHOD: useSuggestedClientGoalAtRetirement()
     * 
     * This method is triggered when the user clicks the apply placeholder button on the
     * Asset Goal at Retirement input. It takes the value of the getClientGloalAtRetirementPlaceholder()
     * method and applies it to the data point in the client data.
     */
    useSuggestedClientGoalAtRetirement() {

      this.$store.dispatch('saveField', {
        field: 'retirement.assetsGoal',
        value: this.getClientGloalAtRetirementPlaceholder(false),
      });     

    },


    /**
     * METHOD: useSuggestedStartingAssets()
     * 
     * This method is triggered when the user clicks the apply placeholder button on the
     * Starting Assets input. It takes the value of the getClientStartingAssetsPlaceholder()
     * method and applies it to the data point in the client data.
     */
    useSuggestedStartingAssets() {

      this.$store.dispatch('saveField', {
        field: 'retirement.statingAssets',
        value: this.getClientStartingAssetsPlaceholder(false),
      });

    },      

      

     /**
     * ---------------------------------------------------------------------------- /
     * 
     * CHARTING METHODS
     *
     * These methods are used to build the data objects required for building the
     * charts used in the Financial GPS report.
     *
     * --------------------------------------------------------------------------- */   


    /**
     * METHOD: chartWealthProjection()
     * @param  {String} view - determines if the view is savings or retirement
     * @return {Object} - returns an object to be used by chart.js 
     *
     * This method builds to build the object that is passed to chart.js in order to create the
     * Financial GPS charts. The view parameter is used to control whether the object includes
     * the pre-retirement (savings) or post-retirement data.
     */
    chartWealthProjection(view) {

      // var view = this.autoCheckGPSView(),
      
      // First set the data based on the view
      var projection = (view === 'savings') ? this.getWealthProjectionData() : this.getWealthDeaccumulationData(),
          adjustment = (view === 'savings') ? this.getWealthProjectionData(true) : this.getWealthDeaccumulationData(true),
          actual     = this.getWealthActualData(),
          chartData = {
            actual: {
              label: 'financialGps.actualValue',
              data: [],
              color: 0,
              spanGaps: true,
            },
            adjusted: {
              label: 'financialGps.adjustedValue',
              data: [],
              color: 5,
              borderWidth: 2,
              pointRadius: 1,
            },
            projected: {
              label: 'financialGps.projectedValue',
              data: [],
              color: 1,
              borderWidth: 2,
              pointRadius: 1,
            },
                       
            labels: []
          },

          dob = this.$store.getters.getClientDOB(),
          mode = this.gpsMode,
          m = 1,
          useAdjusted = this.getBaseField('retirement.useAdjusted');

      if ( ! useAdjusted ) {
        delete chartData.adjusted;
      }

      // Loop over the projection data to add the information into the returned object.
      for ( var x in projection ) {

        chartData.projected.data.push(projection[x]);
        chartData.actual.data.push(actual[x]);
        if ( useAdjusted ) {
          chartData.adjusted.data.push(adjustment[x]);
        }
        if ( mode === 'extended' ) {
          var l = x.substr(1),
              split = l.split('_');

          if ( Number(split[1]) === m ) {
            chartData.labels.push(Number(split[0].substr(0)) - dob.year);
          }
          else {
            chartData.labels.push('');
          }
        }
        else {
          chartData.labels.push(x.substr(1) - dob.year);
        }

      }

      return chartData;

    },


    /**
     * METHOD: chartShockRecovery()
     * @return {Object} - returns an object to be used by chart.js 
     *
     * This method builds to build the object that is passed to chart.js in order to create the
     * Shock Recovery Chart. 
     */
    chartShockRecovery() {

      var lang = this.$store.getters.getCurrentLanguage;
      var chartData = {
        percentages: {
          label: false,
          data: [],
          color: 1,
        },
        labels: [[],[],[]]
      }, 

      data = [];

      var retired = this.isRetired();

      var projection = this.getWealthProjectionData(),
          val = 0;

      if ( ! retired ) { 

        var y = '_0';
        for ( var k in projection ) {

          if ( k > y ) {

            y = k;
            val = Number(projection[k]);

          }

        }

      } else {
        val = this.getClientGloalAtRetirement();            
        val = this.stripFormatCurrency(val);
      }

      var contribute = this.getClientAnnualContributions();
          contribute = this.stripFormatCurrency(contribute);

      var p = 0.05;
      for ( var x = 0; x < 8; x++ ) {

        data[x] = {}

        if ( ! retired ) {
          data[x].data = this.$store.getters.getInterestRateRequired(
              this.$store.getters.getClientYearsToRetire(),
              contribute,
              this.$store.getters.getInitialCapital() * (1 - p),
              val,
              0
          ) * 100;
        } else {
          data[x].data = this.$store.getters.getInterestRateRequired(
            this.$store.getters.getClientWidrawaltoDeath(),
            false,
            val * (1 - p),
            0,
            1 // A type of 1 tells the method to calculate rate through re-accumulation in retirement
          ) * 100; 
        }

        data[x].label = this.formatPercentage(p * -1);

        p += 0.05;

      }

      for ( x = 0; x < data.length; x++ ) {

        chartData.percentages.data[x] = data[x].data;
        chartData.labels[x] = [data[x].label];

      }

      return chartData;

    }, 

  },

  /**
   * COMPUTED
   * --------
   * 
   * These functions are used to return computed values, which
   * can be bound to certain portions of the component.
   */  

  computed: {


    /**
     * FUNCTION: setInitialGPSView()
     *
     * This initial function sets the initial custom transform of the GPS chart. If it
     * is savings, no transform is required. Otherwise, it sets it to the position
     * of the retirement value.
     */
    setInitialGPSView() {

      if ( this.autoCheckGPSView() === 'retirement' ) {

        return 'transform: translate3d(-50%, 0px, 0px)';

      }

      else {

        return '';

      }

    }

  },


  /**
   * View Data
   * The data object for this component contains two properties that help control the display
   * of the GPS chart within the report:
   *
   * gpsView - this parameter controls whether the GPS chart is rendered in pre- or post-retirement.
   * gpsMode - this parameter controls whether the GPS chart attempts to plot an annual average (basic)
   *  or exact values (extended)
   */
  data: function() {

    return {

      gpsView: 'auto',
      gpsMode: 'basic',
      
    }

  },

}

</script>

<style lang="scss">

  .report-ctrl {

    .section__button {

      position: absolute;
      top: .8em;
      right: 1.25em;

      .icon {

        width: 1.6rem;
        height: 1.6rem;

      }

    }

  }

  .report__list-item--checkbox {
    .checkbox-wrap {
      width: 100%;
      line-height: 1em;
    }
  }

</style>




// WEBPACK FOOTER //
// FinancialGPS.vue?47d3f58e